// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useRef, useState } from 'react'
import { useI18n } from '../i18n'
import * as api from '../api'
import { useEventStream } from '../realtime'
import { useRoom } from '../livekit'
import { Room } from './Room'
import { PreJoin, loadDevices, type Devices } from './PreJoin'
import * as embed from '../embed'

/**
 * `prejoin` là trạng thái ĐÃ được nhận vào phòng nhưng CHƯA phát media.
 *
 * Nó tách bạch hai câu hỏi mà bản trước gộp làm một: "tôi có được vào không"
 * (server trả lời) và "tôi đã sẵn sàng cho cả phòng nhìn chưa" (người dùng trả
 * lời). Gộp lại nghĩa là căn phòng chưa dọn được phát trực tiếp trong lúc người
 * ta còn đang tìm nút tắt camera.
 */
type Phase = 'probing' | 'form' | 'prejoin' | 'joining' | 'waiting' | 'in' | 'ended'

type Ctx = {
  sessionID: string
  participantID: string
  principal: string
  workspace: string | null
  role: string
  title: string

  /**
   * Chế độ ghi hình của TRIỂN KHAI, không phải của cuộc họp
   * (ZMEET_RECORDING_MODE). Đi kèm phản hồi join thay vì một endpoint cấu hình
   * riêng: client cần nó đúng một lần, đúng lúc dựng phòng, và một round-trip
   * thứ hai chỉ để hỏi một chuỗi bốn ký tự là thêm một điểm hỏng vào đường vào
   * phòng.
   */
  recordingMode: api.RecordingMode

  /** Phòng đã khoá chưa, lúc vào. Xem api.JoinResponse.locked. */
  locked: boolean
}

/**
 * Meeting nhận MỘT trong hai cách chỉ phòng:
 *
 *   code   mã tham gia 10 chữ số — đường của link mời (`/j/1234567890`)
 *   room   tên phòng do hệ thống ngoài đặt (`/r/support-4821`, ADR-0008)
 *
 * Phần còn lại của màn hình không phân biệt hai đường: cùng chuỗi kiểm tra ở
 * server, cùng phòng chờ, cùng mọi thứ sau khi vào.
 */
export function Meeting({
  code,
  externalRoom,
  account,
  onInRoom,
}: {
  code?: string
  externalRoom?: string
  account: api.Account | null
  /**
   * Báo cho vỏ ứng dụng biết người dùng đã Ở TRONG phòng, để nó thu thanh
   * tiêu đề lại.
   *
   * Chỉ đúng ở phase 'in' — KHÔNG phải ở prejoin hay phòng chờ. Hai màn hình
   * đó vẫn cần bộ chọn ngôn ngữ và nút Đăng nhập: người đang đứng trước cửa
   * có thể cần đổi ngôn ngữ để đọc được câu đang hiện, hoặc đăng nhập để
   * được nhận vào bằng vai trò thành viên.
   */
  onInRoom?: (v: boolean) => void
}) {
  const { t } = useI18n()
  const room = useRoom()

  const [phase, setPhase] = useState<Phase>('probing')
  const [passcode, setPasscode] = useState('')
  const [name, setName] = useState(account?.name ?? '')
  const [msg, setMsg] = useState('')
  const [ctx, setCtx] = useState<Ctx | null>(null)
  const [waiting, setWaiting] = useState<api.Waiting[]>([])
  const [recording, setRecording] = useState(false)
  // Phòng đã khoá chưa. Gieo từ phản hồi join, cập nhật bởi `session.locked` /
  // `session.unlocked` và bởi chính lời gọi của người này — xem nút Khoá ở Room.
  const [locked, setLocked] = useState(false)
  // Đếm số lần một bản ghi phía server tải xong lên kho object. Chỉ là một NHỊP
  // để phòng họp đi hỏi lại API — sự kiện là gợi ý, không phải dữ liệu
  // (ADR-0006 §5), nên ở đây không có id nào được đọc ra từ payload.
  const [recordingReady, setRecordingReady] = useState(0)
  const [consentNeeded, setConsentNeeded] = useState(false)
  // Lời mời bật lại mic/camera do host gửi. Đây là LỜI MỜI, không phải lệnh —
  // xem internal/meeting/moderate.go.
  const [unmuteAsk, setUnmuteAsk] = useState<'audio' | 'video' | null>(null)
  // Bị mời ra khỏi phòng nhưng xin vào lại được. Chỉ có nghĩa ở phase 'ended'.
  const [rejoinable, setRejoinable] = useState(false)
  // Lựa chọn micro/camera, nhớ lại từ lần trước — xem PreJoin.loadDevices.
  const [devices, setDevices] = useState<Devices>(() => loadDevices())

  // Tên đã dùng ở lần gọi /v1/join gần nhất. Người dùng sửa tên ở màn hình
  // prejoin thì phải xin vé lại, nếu không cả phòng thấy cái tên cũ.
  const joinedName = useRef('')

  // Bản sao ref của `ctx`, để enterRoom đọc được giá trị MỚI NHẤT ngay trong
  // cùng một lượt render mà doJoin vừa đặt nó — setState chưa kịp có hiệu lực
  // ở thời điểm ấy, và đó đúng là lúc enterRoom cần nó.
  const ctxRef = useRef<Ctx | null>(null)

  // connectedRef chặn việc kết nối hai lần: `resync` đến cả lúc mở kênh lẫn sau
  // mỗi lần nối lại, và nối vào LiveKit lần thứ hai sẽ đá chính mình ra.
  const connectedRef = useRef(false)

  // participant_id của lần vào phòng ĐANG có hiệu lực. Dùng để vứt bỏ câu trả
  // lời trễ của một lần vào đã kết thúc — xem reconcile.
  const livePartRef = useRef<string | null>(null)

  /**
   * doJoin thử vào phòng.
   *
   * `silent` là phép THĂM DÒ lúc mở trang: phần lớn cuộc họp không đặt mật mã,
   * nên bắt mọi người bấm thêm một nút là thêm ma sát vào đúng việc họ đang
   * làm. Nhưng nếu phép thăm dò đó bị từ chối, ta KHÔNG hiện thông báo — người
   * dùng chưa gõ gì thì "bạn bị từ chối" là một câu vô nghĩa và đáng sợ.
   */
  const doJoin = useCallback(
    async (silent = false) => {
      setPhase('joining')
      setMsg('')
      const r = await api.join({ code, room: externalRoom }, passcode, name)
      const d = r.data

      if (d.outcome === 'rejected') {
        // ⚠️ API cố tình KHÔNG nói vì sao (ADR-0002 bất biến 6): "mã không tồn
        // tại", "sai mật mã" và "bị cấm" trả về cùng một câu và cùng một độ trễ.
        // Đừng đoán hộ người dùng — đoán sai còn tệ hơn không nói.
        setPhase('form')
        if (!silent) setMsg(d.message ?? '')
        return
      }

      // Ghi ref TRƯỚC setCtx: setState là bất đồng bộ, còn ref thì có hiệu lực
      // ngay — và thứ ta cần chặn là một reconcile của lần vào phòng CŨ chạy
      // xong trong khoảng giữa hai thời điểm đó.
      livePartRef.current = d.participant_id ?? null

      const next: Ctx = {
        sessionID: d.session_id ?? '',
        participantID: d.participant_id ?? '',
        principal: d.principal_ref ?? '',
        workspace: d.workspace_id ?? null,
        role: d.role ?? 'attendee',
        title: d.meeting_title ?? '',
        // Vắng mặt = 'local'. Cùng hướng mặc định với server: một client nói
        // chuyện với bản API cũ không được phép vẽ ra một nút tốn tiền.
        recordingMode: d.recording_mode ?? 'local',
        locked: d.locked ?? false,
      }
      ctxRef.current = next
      setCtx(next)
      setLocked(next.locked)

      if (d.outcome === 'waiting') {
        // KHÔNG có token ở nhánh này, và đó là toàn bộ điểm mấu chốt của bất
        // biến số 2. Người đang chờ cũng không có kết nối LiveKit nào — nên
        // thông báo "bạn đã được duyệt" bắt buộc phải đi qua kênh sự kiện
        // (ADR-0006 §1).
        setPhase('waiting')
        setMsg(d.message ?? '')
        return
      }

      // ĐƯỢC NHẬN VÀO, nhưng CHƯA phát gì cả.
      //
      // Bản trước nối media ngay tại đây, và đó là chỗ sinh ra vấn đề mà màn
      // hình prejoin tồn tại để sửa: micro và camera bật lên trước khi người
      // dùng kịp nhìn thấy chúng.
      //
      // `silent` chia hai đường, và sự khác nhau là số lần bấm:
      //
      //	thăm dò lúc mở trang  → dừng ở prejoin, chờ người dùng bấm "Vào phòng"
      //	người dùng vừa bấm    → họ ĐÃ chọn thiết bị ở chính màn hình ấy rồi,
      //	                        bắt bấm lần hai là một thủ tục vô nghĩa
      joinedName.current = name
      if (silent) {
        setPhase('prejoin')
        return
      }
      await enterRoom()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, externalRoom, passcode, name, room, t],
  )

  /**
   * enterRoom nối media và đưa người dùng vào phòng thật.
   *
   * ⚠️ LUÔN xin token MỚI qua `participantStatus` thay vì dùng lại token của
   * lần join. Token sống 60 giây (ADR-0001 §7), còn màn hình prejoin thì người
   * ta ngồi lại bao lâu tuỳ họ — soi lại tóc, đổi camera, tìm tai nghe. Dùng
   * token cũ nghĩa là ai ngần ngừ quá một phút sẽ nhận một lỗi kết nối không
   * giải thích được.
   */
  const enterRoom = useCallback(async () => {
    if (!ctxRef.current) return

    // Đổi tên ở prejoin thì phải xin vé lại: tên nằm TRONG token
    // (media.Grant.Name) và client không tự sửa được sau khi nối (xem
    // livekit.ts). Không xin lại thì cả phòng thấy cái tên cũ.
    if (name !== joinedName.current) {
      await doJoin(false)
      return
    }

    setPhase('joining')
    const c = ctxRef.current
    const r = await api.participantStatus(c.sessionID, c.participantID, c.principal)
    if (!r.ok || !r.data.token || !r.data.livekit_url) {
      setPhase('ended')
      setMsg(t('ui.join.left_queue'))
      return
    }
    setPhase('in')
    if (!connectedRef.current) {
      connectedRef.current = true
      await room.connect(
        r.data.livekit_url,
        r.data.token,
        name || t('ui.join.guest_name'),
        devices,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, room, t, devices])

  /**
   * reconcile hỏi lại SỰ THẬT từ API. Đây là đường DUY NHẤT màn hình này đổi
   * trạng thái phiên — sự kiện chỉ quyết định KHI NÀO gọi nó (ADR-0006 §5).
   */
  const reconcile = useCallback(async () => {
    if (!ctx) return
    const r = await api.participantStatus(ctx.sessionID, ctx.participantID, ctx.principal)

    // ⚠️ Câu trả lời này nói về LẦN VÀO PHÒNG NÀO. Sau khi xin vào lại, ta có
    // một participant_id mới, còn một lời gọi cũ vẫn đang bay về — và lần vào
    // cũ thì mãi mãi ở trạng thái 'removed'. Không có chốt này, câu trả lời trễ
    // ấy đá người dùng ra khỏi phòng họ vừa được duyệt vào.
    if (livePartRef.current !== ctx.participantID) return

    if (!r.ok) {
      setPhase('ended')
      setMsg(t('ui.join.left_queue'))
      return
    }
    const st = r.data
    if (st.state === 'removed' || st.state === 'denied') {
      // Bị mời ra thì gõ cửa lại được; bị chặn thì không. Hai kết cục khác nhau
      // nên phải nói hai câu khác nhau — người bị mời ra nhầm mà nhận được câu
      // "bạn bị từ chối" sẽ đóng tab, và không ai sửa được nữa.
      //
      // 'denied' luôn cho gõ lại: từ chối một người ở phòng chờ KHÔNG ghi lệnh
      // cấm (xem api.deny), nên chặn nút ở đây là giao diện tự dựng ra một luật
      // mà server không có.
      const canRetry = st.state === 'denied' || st.rejoinable === true

      // ⚠️ Phải hạ cờ này, nếu không lần vào lại được duyệt sẽ không bao giờ nối
      // media: cả doJoin lẫn reconcile đều bỏ qua việc nối khi nó còn bật, và
      // kết nối cũ thì đã bị media plane cắt từ lúc bị đuổi.
      connectedRef.current = false
      await room.leave()

      setRejoinable(canRetry)
      setPhase('ended')
      setMsg(t(canRetry ? 'ui.join.removed_can_rejoin' : 'ui.join.denied'))
      return
    }
    if (st.state !== 'admitted' || !st.token || !st.livekit_url) return // vẫn đang chờ
    if (connectedRef.current) return

    connectedRef.current = true
    const next = { ...ctx, role: st.role }
    ctxRef.current = next
    setCtx(next)
    setPhase('in')
    // Lựa chọn thiết bị theo người này vào tận đây. Người ngồi phòng chờ đã tắt
    // camera từ trước; bật lại hộ họ đúng lúc host bấm Duyệt là đúng thứ họ vừa
    // bỏ công tắt đi.
    await room.connect(st.livekit_url, st.token, name || t('ui.join.guest_name'), devices)
  }, [ctx, room, name, t, devices])

  // Rời phòng — một đường duy nhất cho cả nút trong giao diện lẫn lệnh `leave`
  // của trang cha khi Z-Meet đang nằm trong iframe. Hai đường thì một ngày nào
  // đó một đường quên dọn kết nối media.
  const leaveRoom = useCallback(async () => {
    await room.leave()
  }, [room])

  // Cầu postMessage cho trang cha (ADR-0007 §7). Không nằm trong iframe thì
  // init() thoát ngay và không tốn request nào.
  useEffect(() => {
    void embed.init(() => {
      void (async () => {
        await leaveRoom()
        setPhase('ended')
        setMsg(t('ui.grid.left'))
      })()
    })
  }, [leaveRoom, t])

  // Vỏ ứng dụng thu thanh tiêu đề khi ta vào phòng, và trả nó lại khi ta ra —
  // kể cả khi ra bằng cách đóng component. Không dọn ở lần gỡ bỏ thì một người
  // rời phòng bằng nút Back của trình duyệt sẽ ở lại với một trang không có
  // thanh điều hướng nào.
  useEffect(() => {
    onInRoom?.(phase === 'in')
    return () => onInRoom?.(false)
  }, [phase, onInRoom])

  // Báo cho trang cha mỗi lần trạng thái đổi.
  //
  // ⚠️ KHÔNG kèm token, principal ref hay mã tham gia — cùng nguyên tắc với
  // kênh sự kiện (ADR-0006 §5): đây là gợi ý để trang cha dựng giao diện, không
  // phải dữ liệu để nó tin. Muốn dữ liệu thì nó hỏi API bằng API key của nó,
  // ở đó có phân quyền.
  useEffect(() => {
    switch (phase) {
      case 'joining':
        embed.post('joining')
        break
      case 'waiting':
        embed.post('waiting', { session_id: ctx?.sessionID })
        break
      case 'in':
        embed.post('joined', { session_id: ctx?.sessionID, role: ctx?.role })
        break
      case 'ended':
        embed.post('ended', { session_id: ctx?.sessionID, message: msg })
        break
    }
  }, [phase, ctx?.sessionID, ctx?.role, msg])

  // Hàng đợi phòng chờ, cho giao diện của host.
  //
  // Điều kiện là VAI TRÒ, không phải "có workspace hay không". Bản trước dừng ở
  // `if (!ctx?.workspace) return`, nên host của một cuộc gọi mở từ hệ thống
  // ngoài — người luôn là khách — không bao giờ thấy ai đang gõ cửa. Server đã
  // nhận bằng chứng của khách cho endpoint này (xem api.RoomActor).
  //
  // Người không phải host vẫn thoát sớm: gọi cũng chỉ nhận 403, và một request
  // mỗi lần có người vào phòng nhân với số người trong phòng là một cách hào
  // phóng để tự tạo tải.
  const refreshWaiting = useCallback(async () => {
    if (!ctx || !['host', 'cohost'].includes(ctx.role)) return
    const actor: api.RoomActor = ctx.workspace
      ? { workspace: ctx.workspace }
      : { as: ctx.principal, actor: ctx.participantID }
    const r = await api.listWaiting(ctx.sessionID, actor)
    if (r.ok) setWaiting(r.data.waiting ?? [])
  }, [ctx])

  useEventStream(
    ctx?.sessionID ?? null,
    ctx === null
      ? null
      : ctx.workspace
        ? { kind: 'member', workspace: ctx.workspace }
        : { kind: 'guest', principal: ctx.principal, participantID: ctx.participantID },
    {
      onResync: () => {
        void reconcile()
        void refreshWaiting()
      },
      onEvent: (type, payload) => {
        switch (type) {
          case 'session.participant.unmute_requested': {
            const track = (payload as { reason?: string })?.reason
            setUnmuteAsk(track === 'video' ? 'video' : 'audio')
            break
          }
          case 'session.participant.admitted':
          case 'session.participant.removed':
            void reconcile()
            void refreshWaiting()
            break
          case 'session.participant.waiting':
          case 'session.participant.joined':
            void refreshWaiting()
            break
          case 'session.recording.started':
            setRecording(true)
            break
          case 'session.recording.stopping':
            setRecording(false)
            setConsentNeeded(false)
            break
          case 'session.recording.ready':
            // File đã lên xong kho object. ĐÂY mới là lúc link tải dùng được,
            // không phải lúc host bấm Dừng — giữa hai mốc đó egress còn phải
            // hoàn tất file và tải nó lên.
            setRecordingReady((n) => n + 1)
            break
          case 'session.recording.notice':
            setConsentNeeded(true)
            break
          // Chỉ phát ở lúc CHUYỂN trạng thái — không có sự kiện nào nói "phòng
          // vẫn đang khoá". Trạng thái ban đầu đến từ phản hồi join.
          case 'session.locked':
            setLocked(true)
            break
          case 'session.unlocked':
            setLocked(false)
            break
          case 'session.ended':
            setPhase('ended')
            setMsg(t('ui.room.ended'))
            void room.leave()
            break
        }
      },
    },
  )

  // Thăm dò MỘT lần lúc mở trang: cuộc họp không đặt mật mã thì người bấm link
  // mời vào thẳng, không phải bấm thêm nút nào.
  const probed = useRef(false)
  useEffect(() => {
    if (probed.current) return
    probed.current = true
    void doJoin(true)
  }, [doJoin])

  // --- Các trạng thái ngoài phòng -----------------------------------------

  const title = ctx?.title || externalRoom || t('ui.join.heading')

  // Hai màn hình cùng dùng PreJoin, và đó là chủ đích: người phải nhập mật mã
  // và người vào thẳng đều đáng được chọn thiết bị TRƯỚC khi phát, và cả hai
  // đều chỉ phải bấm ĐÚNG MỘT lần.
  //
  //	form     chưa được nhận vào — cần mật mã, hoặc lần thử trước bị từ chối
  //	prejoin  đã được nhận vào, chỉ còn chờ người dùng nói "tôi sẵn sàng"
  if (phase === 'form' || phase === 'prejoin') {
    return (
      <PreJoin
        title={title}
        name={name}
        devices={devices}
        onChange={setDevices}
        busy={false}
        onJoin={() => void (phase === 'prejoin' ? enterRoom() : doJoin())}
      >
        {code && <p className="sub">{t('ui.join.code')}: {code}</p>}

        {/* Mật mã chỉ hỏi ở `form`. Ở `prejoin` thì cửa đã mở rồi — hiện lại ô
            ấy là hỏi một câu đã có câu trả lời.

            Người mang token của hệ thống ngoài KHÔNG phải nhập mật mã (ADR-0008
            §4): token chứng minh một điều mạnh hơn hẳn. */}
        {phase === 'form' && !api.hasAppToken() && (
          <>
            <label htmlFor="pass">{t('ui.join.passcode')}</label>
            <input
              id="pass"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              autoComplete="off"
            />
          </>
        )}

        <label htmlFor="name">{t('ui.join.display_name')}</label>
        <input id="name" value={name} onChange={(e) => setName(e.target.value)} />

        {msg && <div className="msg err">{msg}</div>}
      </PreJoin>
    )
  }

  if (phase !== 'in') {
    return (
      <main className="center">
        <div className="card">
          <h1>{title}</h1>
          {code && <p className="sub">{t('ui.join.code')}: {code}</p>}

          {(phase === 'joining' || phase === 'probing') && (
            <p className="sub">{t('ui.join.calling')}</p>
          )}

          {msg && (
            <div className={'msg ' + (phase === 'waiting' ? 'wait' : 'err')}>{msg}</div>
          )}

          {/* Xin vào lại sau khi bị mời ra. Không tự động thử lại: quay lại một
              phòng vừa đuổi mình ra là một quyết định của con người, và một
              client tự gõ cửa lặp đi lặp lại chính là thứ host bấm nút để dừng.

              Nút này KHÔNG bỏ qua bước nào — nó gọi đúng /v1/join như lần đầu,
              và server ép người từng bị mời ra vào phòng chờ bất kể chính sách.
              Nó chỉ tiết kiệm cho người dùng thao tác tải lại trang. */}
          {phase === 'ended' && rejoinable && (
            <button
              className="wide"
              onClick={() => {
                setRejoinable(false)
                void doJoin()
              }}
            >
              {t('ui.join.ask_rejoin')}
            </button>
          )}
        </div>
      </main>
    )
  }

  // --- Trong phòng ---------------------------------------------------------

  return (
    <Room
      room={room}
      ctx={{
        sessionID: ctx?.sessionID ?? '',
        participantID: ctx?.participantID ?? '',
        principal: ctx?.principal ?? '',
        workspace: ctx?.workspace ?? null,
        role: ctx?.role ?? 'attendee',
        title: ctx?.title ?? '',
      }}
      myName={name || t('ui.join.guest_name')}
      waiting={waiting}
      recording={recording}
      locked={locked}
      onLockedChange={setLocked}
      consentNeeded={consentNeeded}
      recordingMode={ctx?.recordingMode ?? 'local'}
      recordingReady={recordingReady}
      unmuteAsk={unmuteAsk}
      onDismissUnmuteAsk={() => setUnmuteAsk(null)}
      onRefreshWaiting={() => void refreshWaiting()}
      onLeave={async () => {
        await leaveRoom()
        connectedRef.current = false
        setPhase('ended')
        setMsg(t('ui.grid.left'))
      }}
    />
  )
}
